// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore';
import { db, deleteCourseContentCallable } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { ILessonRepository } from './interfaces';
import { CourseRepository } from './course.repository';
import { normalizeMediaFields } from '@shared/video-links';

// Lesson entity interfaces
export interface CreateLessonData {
  title: string;
  description?: string;
  content: string;
  videoUrl?: string;
  durationMinutes?: number;
  moduleId: string;
  courseId: string;
  orderIndex: number;
  isPreview: boolean;
  isPublished: boolean;
  attachments?: string[];
  tags?: string[];
  prerequisites?: string[];
}

export interface UpdateLessonData {
  title?: string;
  description?: string;
  content?: string;
  videoUrl?: string;
  durationMinutes?: number;
  moduleId?: string;
  orderIndex?: number;
  isPreview?: boolean;
  isPublished?: boolean;
  attachments?: string[];
  tags?: string[];
  prerequisites?: string[];
  updatedAt?: Timestamp;
}

export interface LessonFilter {
  moduleId?: string;
  courseId?: string;
  isPreview?: boolean;
  isPublished?: boolean;
  tag?: string;
}

export interface GetLessonData {
  id: string;
  title: string;
  description?: string;
  content: string;
  videoUrl?: string;
  durationMinutes?: number;
  moduleId: string;
  courseId: string;
  orderIndex: number;
  isPreview: boolean;
  isPublished: boolean;
  attachments?: string[];
  tags?: string[];
  prerequisites?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Lessons are stored with `order` -- the field every Cloud Function that
// counts lessons for progress, completion and certificates sorts by, and the
// field every existing lesson document carries. This repository used to write
// and sort by `orderIndex`, which no document has, so orderBy('orderIndex')
// silently dropped every lesson and paid learners saw "0 lessons".
// The app still speaks `orderIndex`; translate at this boundary only.
function toLesson(id: string, data: DocumentData): GetLessonData {
  return { id, ...data, orderIndex: data.orderIndex ?? data.order ?? 0 } as GetLessonData;
}

function withStoredOrder<T extends { orderIndex?: number }>(data: T) {
  const { orderIndex, ...rest } = data;
  return orderIndex === undefined ? rest : { ...rest, order: orderIndex };
}

export class LessonRepository implements ILessonRepository {
  constructor(private db: any) {
    console.log('[LessonRepository] Constructor - Injected Firebase Admin DB instance:', !!this.db);
  }

  private collectionName = 'lessons';

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>,
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // Create lesson
  async createLesson(lessonData: CreateLessonData): Promise<GetLessonData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        // No YouTube Studio links stored: they become the public link (shared/video-links.ts).
        ...normalizeMediaFields(withStoredOrder(lessonData) as unknown as Record<string, unknown>),
        isPreview: lessonData.isPreview ?? false,
        // Published unless told otherwise. Whether learners can see it is
        // the course's own Published switch; the editors have no per-module or
        // per-lesson switch, so a 'false' default made every authored module
        // and lesson invisible to course completion -- 100% done, no
        // certificate, ever.
        isPublished: lessonData.isPublished ?? true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'LESSON_CREATE_MISSING_DOCUMENT',
          devMessage: 'Lesson document was missing after create',
          context: {
            moduleId: lessonData.moduleId,
            courseId: lessonData.courseId,
            title: lessonData.title,
            orderIndex: lessonData.orderIndex,
          },
        });
      }

      return {
        id: docRef.id,
        ...docSnap.data(),
      } as GetLessonData;
    } catch (error) {
      console.error('Error creating lesson:', error);
      throw this.toRepositoryError(error, 'LESSON_CREATE_FAILED', 'Failed to create lesson', {
        moduleId: lessonData.moduleId,
        courseId: lessonData.courseId,
        title: lessonData.title,
        orderIndex: lessonData.orderIndex,
        isPreview: lessonData.isPreview,
        isPublished: lessonData.isPublished,
      });
    }
  }

  // Get lesson by ID
  async getLessonById(lessonId: string): Promise<GetLessonData | null> {
    try {
      const docRef = doc(db, this.collectionName, lessonId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return toLesson(docSnap.id, docSnap.data());
    } catch (error) {
      console.error('Error getting lesson by ID:', error);
      throw this.toRepositoryError(error, 'LESSON_GET_FAILED', 'Failed to get lesson by ID', {
        lessonId,
      });
    }
  }

  // Get lessons with filters
  async getLessons(filter: LessonFilter = {}, limitCount?: number): Promise<GetLessonData[]> {
    try {
      let q = collection(db, this.collectionName);

      // Build query with filters
      if (filter.moduleId) {
        q = query(q, where('moduleId', '==', filter.moduleId));
      }
      if (filter.courseId) {
        q = query(q, where('courseId', '==', filter.courseId));
      }
      if (filter.isPreview !== undefined) {
        q = query(q, where('isPreview', '==', filter.isPreview));
      }
      if (filter.isPublished !== undefined) {
        q = query(q, where('isPublished', '==', filter.isPublished));
      }
      if (filter.tag) {
        q = query(q, where('tags', 'array-contains', filter.tag));
      }

      // Add ordering and limit
      q = query(q, orderBy('order', 'asc'));
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const lessons: GetLessonData[] = [];

      querySnapshot.forEach((doc) => {
        lessons.push(toLesson(doc.id, doc.data()));
      });

      return lessons;
    } catch (error) {
      console.error('Error getting lessons:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_FAILED', 'Failed to get lessons', {
        filter,
        limitCount,
      });
    }
  }

  // Update lesson
  async updateLesson(lessonId: string, updateData: UpdateLessonData): Promise<GetLessonData> {
    try {
      const docRef = doc(db, this.collectionName, lessonId);
      await updateDoc(docRef, {
        ...normalizeMediaFields(withStoredOrder(updateData) as unknown as Record<string, unknown>),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'LESSON_UPDATE_MISSING_DOCUMENT',
          devMessage: 'Lesson was missing after update',
          context: {
            lessonId,
            updateData,
          },
        });
      }

      return toLesson(docSnap.id, docSnap.data());
    } catch (error) {
      console.error('Error updating lesson:', error);
      throw this.toRepositoryError(error, 'LESSON_UPDATE_FAILED', 'Failed to update lesson', {
        lessonId,
        updateData,
      });
    }
  }

  // Delete lesson
  async deleteLesson(lessonId: string): Promise<void> {
    try {
      // Deleting only this document left everything under it -- modules,
      // lessons, quizzes, questions, material records -- and every uploaded
      // file behind. The server deletes the whole tree and its files, and
      // checks the caller owns the course (the rules only let admins delete).
      await deleteCourseContentCallable({ scope: 'lesson', id: lessonId });
    } catch (error) {
      console.error('Error deleting lesson:', error);
      throw this.toRepositoryError(error, 'LESSON_DELETE_FAILED', 'Failed to delete lesson', {
        lessonId,
      });
    }
  }

  // Get lesson by title
  async getLessonByTitle(title: string): Promise<GetLessonData | null> {
    try {
      const q = query(collection(db, this.collectionName), where('title', '==', title));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return toLesson(doc.id, doc.data());
    } catch (error) {
      console.error('Error getting lesson by title:', error);
      throw this.toRepositoryError(error, 'LESSON_GET_BY_TITLE_FAILED', 'Failed to get lesson by title', {
        title,
      });
    }
  }

  // Get lessons by module
  async getLessonsByModule(moduleId: string): Promise<GetLessonData[]> {
    try {
      const filter: LessonFilter = { moduleId: moduleId };
      return this.getLessons(filter);
    } catch (error) {
      console.error('Error getting lessons by module:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_BY_MODULE_FAILED', 'Failed to get lessons by module', {
        moduleId,
      });
    }
  }

  // Get lessons by course
  async getLessonsByCourse(courseId: string): Promise<GetLessonData[]> {
    try {
      const filter: LessonFilter = { courseId: courseId };
      return this.getLessons(filter);
    } catch (error) {
      console.error('Error getting lessons by course:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_BY_COURSE_FAILED', 'Failed to get lessons by course', {
        courseId,
      });
    }
  }

  // Get preview lessons
  async getPreviewLessons(limitCount?: number): Promise<GetLessonData[]> {
    try {
      const filter: LessonFilter = { isPreview: true, isPublished: true };
      return this.getLessons(filter, limitCount);
    } catch (error) {
      console.error('Error getting preview lessons:', error);
      throw this.toRepositoryError(error, 'LESSON_PREVIEW_LIST_FAILED', 'Failed to get preview lessons', {
        limitCount,
      });
    }
  }

  // Get lessons by tag
  async getLessonsByTag(tagId: string, limitCount?: number): Promise<GetLessonData[]> {
    try {
      const filter: LessonFilter = { tag: tagId };
      return this.getLessons(filter, limitCount);
    } catch (error) {
      console.error('Error getting lessons by tag:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_BY_TAG_FAILED', 'Failed to get lessons by tag', {
        tagId,
        limitCount,
      });
    }
  }

  // Get lessons by owner (course instructor)
  async getLessonsByOwner(ownerId: string, limitCount?: number): Promise<GetLessonData[]> {
    try {
      // First get courses by instructor, then get lessons for those courses
      const courseRepo = new CourseRepository();
      const courses = await courseRepo.getCoursesByInstructor(ownerId);
      const courseIds = courses.map(course => course.id);

      if (courseIds.length === 0) {
        return [];
      }

      // Get lessons for all courses by this instructor
      const lessons: GetLessonData[] = [];
      for (const courseId of courseIds) {
        const courseLessons = await this.getLessonsByCourse(courseId);
        lessons.push(...courseLessons);
      }

      return lessons.slice(0, limitCount || lessons.length);
    } catch (error) {
      console.error('Error getting lessons by owner:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_BY_OWNER_FAILED', 'Failed to get lessons by owner', {
        ownerId,
        limitCount,
      });
    }
  }

  // Get all lessons (admin only) or owner's lessons
  async getLessonsForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetLessonData[]> {
    try {
      if (isAdmin) {
        // Admin sees all lessons
        return this.getLessons({}, limitCount);
      } else {
        // Regular user sees only their own lessons
        return this.getLessonsByOwner(userId, limitCount);
      }
    } catch (error) {
      console.error('Error getting lessons for user:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_FOR_USER_FAILED', 'Failed to get lessons for user', {
        userId,
        isAdmin,
        limitCount,
      });
    }
  }

  // Get all lessons (admin only)
  async getAllLessons(limitCount?: number): Promise<GetLessonData[]> {
    try {
      return this.getLessons({}, limitCount);
    } catch (error) {
      console.error('Error getting all lessons:', error);
      throw this.toRepositoryError(error, 'LESSON_LIST_ALL_FAILED', 'Failed to get all lessons', {
        limitCount,
      });
    }
  }

  // Upload lesson video
  async uploadLessonVideo(lessonId: string, file: File): Promise<string> {
    try {
      console.log('Uploading lesson video for lesson:', lessonId, 'File:', file.name);

      // Simulate upload delay (videos take longer)
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Generate a mock URL
      const videoUrl = `https://storage.example.com/lessons/${lessonId}/video_${Date.now()}_${file.name}`;

      // Update lesson with new video URL
      await this.updateLesson(lessonId, { videoUrl: videoUrl });

      return videoUrl;
    } catch (error) {
      console.error('Error uploading lesson video:', error);
      throw this.toRepositoryError(error, 'LESSON_VIDEO_UPLOAD_FAILED', 'Failed to upload lesson video', {
        lessonId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
    }
  }

  // Upload lesson attachment/image
  async uploadLessonAttachment(lessonId: string, file: File): Promise<string> {
    try {
      console.log('Uploading lesson attachment for lesson:', lessonId, 'File:', file.name);

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate a mock URL
      const attachmentUrl = `https://storage.example.com/lessons/${lessonId}/attachment_${Date.now()}_${file.name}`;

      // Get current lesson to append to attachments array
      const lesson = await this.getLessonById(lessonId);
      if (!lesson) {
        throw new DRXError({
          code: 'LESSON_ATTACHMENT_UPLOAD_MISSING_LESSON',
          devMessage: 'Lesson was not found before uploading attachment',
          context: {
            lessonId,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          },
        });
      }

      const currentAttachments = lesson.attachments || [];
      const updatedAttachments = [...currentAttachments, attachmentUrl];

      // Update lesson with new attachments array
      await this.updateLesson(lessonId, { attachments: updatedAttachments });

      return attachmentUrl;
    } catch (error) {
      console.error('Error uploading lesson attachment:', error);
      throw this.toRepositoryError(error, 'LESSON_ATTACHMENT_UPLOAD_FAILED', 'Failed to upload lesson attachment', {
        lessonId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
    }
  }

  // Delete lesson video
  async deleteLessonVideo(lessonId: string): Promise<void> {
    try {
      const lesson = await this.getLessonById(lessonId);
      if (!lesson?.videoUrl) {
        return; // No video to delete
      }

      // In real implementation, delete from cloud storage
      console.log('Deleting lesson video:', lesson.videoUrl);

      // Update lesson to remove video URL
      await this.updateLesson(lessonId, { videoUrl: null });
    } catch (error) {
      console.error('Error deleting lesson video:', error);
      throw this.toRepositoryError(error, 'LESSON_VIDEO_DELETE_FAILED', 'Failed to delete lesson video', {
        lessonId,
      });
    }
  }

  // Delete lesson attachment
  async deleteLessonAttachment(lessonId: string, attachmentUrl: string): Promise<void> {
    try {
      const lesson = await this.getLessonById(lessonId);
      if (!lesson?.attachments) {
        return; // No attachments
      }

      // In real implementation, delete from cloud storage
      console.log('Deleting lesson attachment:', attachmentUrl);

      // Remove attachment from array
      const updatedAttachments = lesson.attachments.filter(url => url !== attachmentUrl);

      // Update lesson with filtered attachments
      await this.updateLesson(lessonId, { attachments: updatedAttachments });
    } catch (error) {
      console.error('Error deleting lesson attachment:', error);
      throw this.toRepositoryError(error, 'LESSON_ATTACHMENT_DELETE_FAILED', 'Failed to delete lesson attachment', {
        lessonId,
        attachmentUrl,
      });
    }
  }

  // Get published lessons
  async getPublishedLessons(limitCount?: number): Promise<GetLessonData[]> {
    try {
      const filter: LessonFilter = { isPublished: true };
      return this.getLessons(filter, limitCount);
    } catch (error) {
      console.error('Error getting published lessons:', error);
      throw this.toRepositoryError(error, 'LESSON_PUBLISHED_LIST_FAILED', 'Failed to get published lessons', {
        limitCount,
      });
    }
  }

  // Reorder lessons in a module
  async reorderLessons(moduleId: string, lessonOrders: { lessonId: string; orderIndex: number }[]): Promise<void> {
    try {
      const updatePromises = lessonOrders.map(({ lessonId, orderIndex }) =>
        updateDoc(doc(db, this.collectionName, lessonId), {
          order: orderIndex,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error reordering lessons:', error);
      throw this.toRepositoryError(error, 'LESSON_REORDER_FAILED', 'Failed to reorder lessons', {
        moduleId,
        lessonOrders,
      });
    }
  }

  // Get next lesson in module
  async getNextLesson(currentLessonId: string): Promise<GetLessonData | null> {
    try {
      const currentLesson = await this.getLessonById(currentLessonId);
      if (!currentLesson) {
        return null;
      }

      const moduleLessons = await this.getLessonsByModule(currentLesson.moduleId);
      const currentIndex = moduleLessons.findIndex(lesson => lesson.id === currentLessonId);

      if (currentIndex === -1 || currentIndex === moduleLessons.length - 1) {
        return null;
      }

      return moduleLessons[currentIndex + 1];
    } catch (error) {
      console.error('Error getting next lesson:', error);
      throw this.toRepositoryError(error, 'LESSON_NEXT_FAILED', 'Failed to get next lesson', {
        currentLessonId,
      });
    }
  }

  // Get previous lesson in module
  async getPreviousLesson(currentLessonId: string): Promise<GetLessonData | null> {
    try {
      const currentLesson = await this.getLessonById(currentLessonId);
      if (!currentLesson) {
        return null;
      }

      const moduleLessons = await this.getLessonsByModule(currentLesson.moduleId);
      const currentIndex = moduleLessons.findIndex(lesson => lesson.id === currentLessonId);

      if (currentIndex <= 0) {
        return null;
      }

      return moduleLessons[currentIndex - 1];
    } catch (error) {
      console.error('Error getting previous lesson:', error);
      throw this.toRepositoryError(error, 'LESSON_PREVIOUS_FAILED', 'Failed to get previous lesson', {
        currentLessonId,
      });
    }
  }
}
